import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Table, FileIcon, ImportIcon } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import ACL_RELATIONSHIPS from "acl-relationships";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import CustomTableService from "services/CustomTable";
import {
  CUSTOM_TABLES_ADMIN_TABLE_CONSTANTS,
  invalidateCustomTablesQuery,
  useCustomTablesAdminGrid,
} from "hooks/useCustomTablesAdminGrid";
import DeleteTableModal from "routes/custom_tables/modals/DeleteTableModal";
import { customTablesAdminKeys } from "routes/custom_tables/settings/queryKeys";

// Built once: a fresh array on every render would make the table recompute its
// selected-rows memo, since that is keyed on the root's identity.
const GRID_QUERY_KEY = customTablesAdminKeys.list();

const CustomTablesAdmin = () => {
  const navigate = useNavigate();
  const deleteModalRef = useRef(null);
  const canManageTables = useAuthorized([
    ACL_RELATIONSHIPS.customTable.editSchema,
  ]);

  const handleEdit = useCallback(
    table => {
      if (!table?.slug) return;
      navigate(`/settings/custom_tables/${table.slug}/edit`);
    },
    [navigate]
  );

  const handleDelete = useCallback(table => {
    if (!table?.slug || !deleteModalRef.current) return;
    deleteModalRef.current.open({
      slug: table.slug,
      displayName: table.display_name,
    });
  }, []);

  const { columns, filters } = useCustomTablesAdminGrid({
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  const fetchCustomTables = useCallback(
    async queryParams => {
      const nextQueryParams = getNextQueryParams(queryParams, columns);
      return await CustomTableService.list(nextQueryParams);
    },
    [columns]
  );

  const { data: emptyCheckData, isFetched: emptyCheckFetched } = useQuery({
    queryKey: customTablesAdminKeys.emptyCheck(),
    queryFn: () => CustomTableService.list(),
  });

  const isEmpty =
    emptyCheckFetched &&
    emptyCheckData &&
    (emptyCheckData.total === 0 ||
      (Array.isArray(emptyCheckData.data) && emptyCheckData.data.length === 0));

  return (
    <>
      <DeleteTableModal ref={deleteModalRef} />
      {isEmpty ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Table />
            </EmptyMedia>
            <EmptyTitle>No custom tables yet</EmptyTitle>
            <EmptyDescription>
              Create your first custom table to store reusable lookup data such
              as UTM sources, statuses, or tags.
            </EmptyDescription>
          </EmptyHeader>
          {canManageTables && (
            <EmptyContent>
              <DropdownMenu>
                <DropdownMenuTrigger className={buttonVariants()}>
                  Create New Table
                </DropdownMenuTrigger>
                <DropdownMenuContent className={"tw:w-48 tw:cursor-pointer"}>
                  <DropdownMenuItem
                    onClick={() => navigate("/settings/custom_tables/new")}
                  >
                    <FileIcon />
                    Create from Form
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate("/custom_tables/import?mode=new_table")
                    }
                  >
                    <ImportIcon />
                    Create from Import
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </EmptyContent>
          )}
        </Empty>
      ) : (
        <BaseTableV3
          columns={columns}
          filters={filters}
          enableSearch
          tableName={CUSTOM_TABLES_ADMIN_TABLE_CONSTANTS.tableName}
          queryKey={GRID_QUERY_KEY}
          className={CUSTOM_TABLES_ADMIN_TABLE_CONSTANTS.className}
          header={CUSTOM_TABLES_ADMIN_TABLE_CONSTANTS.header}
          headerIcon={CUSTOM_TABLES_ADMIN_TABLE_CONSTANTS.headerIcon}
          createButton={
            canManageTables ? (
              <DropdownMenu>
                <DropdownMenuTrigger className={buttonVariants()}>
                  Create New Table
                </DropdownMenuTrigger>
                <DropdownMenuContent className={"tw:w-48 tw:cursor-pointer"}>
                  <DropdownMenuItem
                    onClick={() => navigate("/settings/custom_tables/new")}
                  >
                    <FileIcon />
                    Create from Form
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate("/custom_tables/import?mode=new_table")
                    }
                  >
                    <ImportIcon />
                    Create from Import
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null
          }
          fetchData={fetchCustomTables}
          invalidateData={invalidateCustomTablesQuery}
        />
      )}
    </>
  );
};

export default CustomTablesAdmin;
